// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import resetReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ResetReCaptcha";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { RefObject, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    location: string;
    recaptchaRef: RefObject<ReCAPTCHA | null>;
    extendedStyles?: ReCaptchaStyles;
    // XNG-Changes
    forceEnable?: boolean;
    displayForAuthenticated?: boolean;
}

const mapStateToProps = (state: ApplicationState, { location, recaptchaRef, forceEnable }: OwnProps) => {
    const { websiteSettings } = getSettingsCollection(state);
    const session = getSession(state);
    return {
        siteKey: websiteSettings.reCaptchaSiteKey,
        // XNG-Changes:  forceEnable
        isEnabled: forceEnable || (websiteSettings as { [key: string]: any })[`reCaptchaEnabledFor${location}`],
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        errorMessage: state.components.reCaptcha.errorMessage[location],
        recaptchaRef,
        location,
    };
};

const mapDispatchToProps = {
    validateReCaptcha,
    resetReCaptcha,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ReCaptchaStyles {
    wrapper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
}

export const reCaptchaStyles: ReCaptchaStyles = {
    wrapper: {
        css: css`
            min-width: 304px;
            min-height: 78px;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 5px;
            font-size: 15px;
            font-weight: 600;
        `,
    },
};

const ReCaptcha = ({
    siteKey,
    isEnabled,
    isAuthenticated,
    errorMessage,
    recaptchaRef,
    location,
    validateReCaptcha,
    resetReCaptcha,
    extendedStyles,
    // XNG-Change
    displayForAuthenticated,
}: Props) => {
    const [styles] = useState(() => mergeToNew(reCaptchaStyles, extendedStyles));
    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        setFirstRender(false);
        resetReCaptcha({ location });
    }, []);

    const handleSuccess = () => {
        validateReCaptcha({ location, recaptchaRef });
    };

    if (
        firstRender ||
        !isEnabled ||
        (isAuthenticated && !displayForAuthenticated) ||
        !!getCookie("g-recaptcha-verified")
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ReCAPTCHA ref={recaptchaRef} sitekey={siteKey} onChange={handleSuccess} />
            {errorMessage && (
                <Typography as="p" {...styles.errorMessageText}>
                    {errorMessage}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReCaptcha);
